import { User, UserGroup } from 'types';
import { store } from 'shared/store';
import { selectActiveUser } from 'shared/store/shared-selectors';
import {
  SchedulerCalendarEventTypeHost,
  SchedulerCalendarEventTypeMode,
} from 'sb-lib/types';
import { selectActiveOrgMembers } from 'shared/store/auth-slice';
import { EventCategoryType } from 'browser/components/Scheduling/SchedulingEventTypesList/SchedulingEventTypesListContent/SchedulingEventTypeCategoryBadge';
import { getLandingPageUrl } from 'shared/settings';

export type FilterParams = {
  users: User[];
  userGroups: UserGroup[];
  selectedFilter: FilterId;
};
export enum FilterItemType {
  GROUP = 'group',
  USER = 'user',
  ALL_USERS = 'all_users',
  ALL_GROUPS = 'all_groups',
}
export type FilterId = {
  id: number | string;
  type: FilterItemType;
};

export type FilterItem = FilterId & {
  displayName: string;
};

export const getUserIdsFromFilterParams = (params: FilterParams): number[] | null => {
  const filter = params.selectedFilter;
  const type = filter.type;

  if (type === FilterItemType.USER) {
    if (typeof filter.id === 'number') {
      // Single user
      return [filter.id];
    } else if (filter.id === 'me') {
      const state = store.getState();
      const user = selectActiveUser(state);
      if (user) {
        // Me
        return [user.id];
      }
    }
  } else if (type === FilterItemType.GROUP) {
    const group = params.userGroups.find(g => g.id === filter.id);
    if (group) {
      // One group
      return group.users.map(u => u.id);
    }
  } else if (type === FilterItemType.ALL_GROUPS) {
    //All groups
    return [
      ...new Set(
        params.userGroups
          .map(g => g.users)
          .flat()
          .map(u => u.id),
      ),
    ];
  }

  return null;
};

export const getCategoryType = (eventType: {
  mode: SchedulerCalendarEventTypeMode;
  hosts: any[];
}): EventCategoryType => {
  // availability and fair types are always round robin
  if (
    eventType.mode == SchedulerCalendarEventTypeMode.availability ||
    eventType.mode == SchedulerCalendarEventTypeMode.fair
  ) {
    return 'round-robin';
  }
  // collective types are determined by the number of hosts
  if (eventType.hosts.length === 1) {
    return 'one-on-one';
  } else {
    return 'collective';
  }
};

export const getDisplayType = (eventType: {
  mode: SchedulerCalendarEventTypeMode;
  hosts: any[];
}): string => {
  let displayType: string;
  if (eventType.mode === SchedulerCalendarEventTypeMode.collective) {
    if (eventType.hosts.length === 1) {
      displayType = 'One on One meeting';
    } else {
      displayType = 'Collective meeting';
    }
  } else if (eventType.mode == SchedulerCalendarEventTypeMode.availability) {
    displayType = 'Optimize for Availability';
  } else {
    displayType = 'Optimize for Fairness';
  }

  return displayType;
};

export const getUsersFromEventType = (eventType: {
  hosts: SchedulerCalendarEventTypeHost[];
}): User[] => {
  const state = store.getState();
  const users = selectActiveOrgMembers(state);
  const hostUserIds = eventType.hosts.map(h => h.userId);
  return users.filter(u => hostUserIds.includes(u.id));
};

export const isCollective = (eventType: {
  mode: SchedulerCalendarEventTypeMode;
  hosts: any[];
}): boolean => {
  return (
    eventType.mode === SchedulerCalendarEventTypeMode.collective &&
    eventType.hosts.length > 1
  );
};

export const getMeetingOrigin = () => {
  return getLandingPageUrl() || location.origin;
};
